import {
  Badge,
  Box,
  Flex,
  Grid,
  GridItem,
  Link,
  LinkProps,
  SystemProps,
  Text,
  TextProps,
  ThemingProps,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { Children } from "react";

import {
  IcoCheckCircle,
  IcoCircle,
  IcoGitClosedPR,
  IcoGitMergedPR,
  IcoGitOpenPR,
} from "@/common/components/Icons";
import { IconSizesType } from "@/theme/Icon.theme";

type CheckIconProps = {
  value: boolean;
  size?: IconSizesType;
};

export function CheckIcon({ value, size = "md" }: CheckIconProps) {
  const iconSuccessColor = useColorModeValue("brand.400", "brand.400");
  const iconIntermediateColor = useColorModeValue("yellow.600", "yellow.400");
  return (
    <>
      {value ? (
        <Box color={iconSuccessColor}>
          <IcoCheckCircle size={size} />
        </Box>
      ) : (
        <Box color={iconIntermediateColor}>
          <IcoCircle size={size} />
        </Box>
      )}
    </>
  );
}

export function CheckListItem({
  title,
  isChecked,
  children,
  contentHidden = false,
  rowGap = 1.5,
  _title,
  _checkIcon,
}: {
  title: string;
  isChecked: boolean;
  children?: React.ReactNode;
  isLast?: boolean;
  contentHidden?: boolean;
  rowGap?: SystemProps["rowGap"];
  _title?: TextProps;
  _checkIcon?: Omit<CheckIconProps, "value">;
}) {
  return (
    <Grid columnGap={2} rowGap={rowGap} templateColumns={"auto 1fr"}>
      <GridItem alignSelf={"center"} justifySelf="center">
        <CheckIcon value={isChecked} {..._checkIcon} />
      </GridItem>
      <GridItem alignSelf={"center"}>
        <Text fontSize="sm" fontWeight="medium" {..._title}>
          {title}
        </Text>
      </GridItem>
      {Children.toArray(children).length > 0 && (
        <>
          <GridItem justifySelf="center" />
          <GridItem>{!contentHidden && children}</GridItem>
        </>
      )}
    </Grid>
  );
}

export function PullRequestStatusBadge({
  status,
}: {
  status: "closed" | "open" | "merged" | "creating" | "error";
}) {
  let badgeProps: {
    colorScheme: ThemingProps["colorScheme"];
    icon: React.ReactNode;
    text: string;
  } | null = null;

  switch (status) {
    case "closed":
      badgeProps = {
        colorScheme: "red",
        icon: <IcoGitClosedPR size="sm" />,
        text: "Closed",
      };
      break;
    case "open":
      badgeProps = {
        colorScheme: "green",
        icon: <IcoGitOpenPR size="sm" />,
        text: "Open",
      };
      break;
    case "merged":
      badgeProps = {
        colorScheme: "purple",
        icon: <IcoGitMergedPR size="sm" />,
        text: "Merged",
      };
      break;
    case "creating":
      badgeProps = {
        colorScheme: "gray",
        icon: <IcoGitOpenPR size="sm" />,
        text: "Creating",
      };
      break;
    case "error":
      badgeProps = {
        colorScheme: "red",
        icon: <IcoGitClosedPR size="sm" />,
        text: "Error",
      };
  }

  return (
    <Flex maxW="xs">
      <Badge
        alignItems="center"
        color="white"
        colorScheme={badgeProps?.colorScheme}
        display="flex"
        flexDirection="row"
        gap={1}
        size="sm"
        variant="solid"
      >
        {badgeProps?.icon}
        <Text>{badgeProps?.text}</Text>
      </Badge>
    </Flex>
  );
}

export function FormatPR({
  number,
  title,
}: {
  number?: number;
  title?: string;
}) {
  return (
    <>
      {number && (
        <Text fontWeight="semibold">
          {"#"}
          {number}
        </Text>
      )}
      {title && (
        <Text
          display="block"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {title?.replaceAll("`", "")}
        </Text>
      )}
    </>
  );
}
export function PullRequestLink({
  url,
  status,
  number,
  title,
  ...rest
}: {
  url: string;
  status: "closed" | "open" | "merged" | "creating" | "error";
  number?: number;
  title?: string;
} & LinkProps) {
  return (
    <Flex
      align="center"
      gap={2}
      maxW="xs"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(url, "_blank");
      }}
    >
      <PullRequestStatusBadge status={status} />
      <Link
        align="center"
        display="flex"
        gap={1}
        href={url}
        maxW="2xs"
        textDecoration="none"
        variant="unstyled"
        isExternal
        {...rest}
      >
        <FormatPR number={number} title={title} />
      </Link>
    </Flex>
  );
}
